import { useState, useEffect } from "react";
import { Form, Button, Row, Col, Card } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { message } from "antd";
import {
  useGetClientByIdQuery,
  useUpdateClientMutation,
} from "../../../redux/api/ClientsApi/clientsApi";
import { FaSave, FaArrowLeft, FaEdit, FaImage } from "react-icons/fa";
import "../Services/Services.css";
import LoaderSpinner from "../../../Components/LoaderSpinner";
import { useFormik } from "formik";
import * as Yup from "yup";

const EditClients = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: client, isLoading: isFetching } = useGetClientByIdQuery(id);
  const [updateClient, { isLoading: isUpdating }] = useUpdateClientMutation();

  const [imagePreview, setImagePreview] = useState(null);
  const [existingImage, setExistingImage] = useState("");

  useEffect(() => {
    if (client) {
      setExistingImage(client.client_logo || "");
    }
  }, [client]);

  const validationSchema = Yup.object({
    client_name: Yup.string().trim().required("Client name is required"),
    display_order: Yup.number(),
    status: Yup.boolean(),
    client_logo: Yup.mixed().test(
      "required",
      "Client logo is required",
      function (value) {
        if (existingImage) return true;
        return !!value;
      },
    ),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      client_name: client?.client_name || "",
      display_order: client?.display_order || 0,
      status: client?.status ?? true,
      client_logo: null,
    },
    validationSchema,
    onSubmit: async (values) => {
      const data = new FormData();
      Object.keys(values).forEach((key) => {
        if (key === "client_logo") {
          if (values[key]) {
            data.append(key, values[key]);
          }
        } else {
          data.append(key, values[key]);
        }
      });

      try {
        await updateClient({ id, data }).unwrap();
        message.success("Client updated successfully!");
        setTimeout(() => navigate("/admin/clients"), 2000);
      } catch (error) {
        message.error(error.data?.error || "Failed to update client");
      }
    },
  });

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      formik.setFieldValue("client_logo", file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  if (isFetching || isUpdating) {
    return (
      <div
        className="d-flex justify-content-center align-items-center bg-light"
        style={{ height: "100vh" }}
      >
        <LoaderSpinner />
      </div>
    );
  }

  return (
    <div className="container py-4 fade-in">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="fw-bold text-dark mb-1">
            <FaEdit className="text-primary me-2" />
            Edit Client
          </h2>
          <p className="text-muted mb-0">Modify client details</p>
        </div>
        <Button
          variant="outline-secondary"
          className="d-flex align-items-center gap-2 shadow-sm rounded-pill px-4"
          onClick={() => navigate("/admin/clients")}
        >
          <FaArrowLeft /> Back to List
        </Button>
      </div>

      <Form onSubmit={formik.handleSubmit}>
        <Card className="border-0 overflow-hidden">
          <Card.Body className="p-4 p-md-5">
            <h5 className="mb-4 text-secondary border-start border-4 border-primary ps-3">
              Client Information
            </h5>
            <Row className="g-4">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold">
                    Client Name <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="client_name"
                    {...formik.getFieldProps("client_name")}
                    isInvalid={
                      formik.touched.client_name && !!formik.errors.client_name
                    }
                    placeholder="e.g. Google"
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.client_name}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold">Display Order</Form.Label>
                  <Form.Control
                    type="number"
                    name="display_order"
                    {...formik.getFieldProps("display_order")}
                  />
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Group>
                  <Form.Label className="fw-semibold">Client Logo</Form.Label>
                  <div className="custom-file-container">
                    <input
                      type="file"
                      id="client-logo-input-edit"
                      accept="image/*"
                      onChange={handleFileChange}
                      hidden
                    />
                    <label
                      htmlFor="client-logo-input-edit"
                      className={`d-flex align-items-center gap-2 border rounded-3 p-1 cursor-pointer bg-white shadow-sm ${formik.touched.client_logo && formik.errors.client_logo ? "border-danger" : ""}`}
                      style={{ minHeight: "45px" }}
                    >
                      <div className="btn btn-primary btn-sm rounded-pill px-3 ms-1">
                        Choose File
                      </div>
                      <span className="text-muted small text-truncate px-2 flex-grow-1">
                        {formik.values.client_logo
                          ? formik.values.client_logo.name
                          : existingImage
                            ? existingImage.split("/").pop()
                            : "No file chosen"}
                      </span>
                      <div className="pe-3 text-primary">
                        <FaImage />
                      </div>
                    </label>
                  </div>
                  {formik.touched.client_logo && formik.errors.client_logo && (
                    <div className="text-danger small mt-1">
                      {formik.errors.client_logo}
                    </div>
                  )}

                  <div className="mt-3 d-flex gap-3">
                    {existingImage && !imagePreview && (
                      <div>
                        <small className="d-block text-muted mb-1">
                          Current Logo:
                        </small>
                        <img
                          src={existingImage}
                          alt="Current"
                          className="rounded shadow-sm border p-1"
                          style={{
                            maxWidth: "200px",
                            maxHeight: "100px",
                            objectFit: "contain",
                          }}
                        />
                      </div>
                    )}
                    {imagePreview && (
                      <div>
                        <small className="d-block text-muted mb-1">
                          New Logo Preview:
                        </small>
                        <img
                          src={imagePreview}
                          alt="Preview"
                          className="rounded shadow-sm border p-1"
                          style={{
                            maxWidth: "200px",
                            maxHeight: "100px",
                            objectFit: "contain",
                          }}
                        />
                      </div>
                    )}
                  </div>
                </Form.Group>
              </Col>

              <Col md={12} className="d-flex align-items-center">
                <Form.Group>
                  <Form.Check
                    type="switch"
                    id="client-status"
                    label="Active / Published"
                    name="status"
                    checked={formik.values.status}
                    onChange={formik.handleChange}
                    className="fw-bold text-primary"
                  />
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex justify-content-end gap-3 mt-5">
              <Button
                variant="light"
                className="rounded-pill px-4"
                onClick={() => navigate("/admin/clients")}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                className="rounded-pill px-5 shadow-sm d-flex align-items-center gap-2"
              >
                <FaSave /> Save Changes
              </Button>
            </div>
          </Card.Body>
        </Card>
      </Form>
    </div>
  );
};

export default EditClients;
